/**
 * Centralized API URL Configuration
 * 
 * All API base URLs are resolved directly from environment variables.
 * This is the single source of truth for all backend service URLs.
 */

// Helper function to resolve an absolute URL from an environment variable.
const resolveUrl = (envVar, fallbackPath) => {
  const envUrl = process.env[envVar];

  // Primary case: The .env variable is a valid, absolute URL. Use it directly.
  if (envUrl && envUrl.startsWith('http')) {
    return envUrl;
  }
  
  // Log a warning if the variable is missing or invalid.
  if (envUrl) {
    console.error(`CRITICAL: ${envVar}="${envUrl}" is not a valid absolute URL. Please check your .env file.`);
  } else {
    console.warn(`WARN: ${envVar} is not defined in your .env file. Falling back to a default relative path.`);
  }

  // Fallback to a relative path based on the current window origin.
  // ---- Important : This might work in some proxy setups but is not ideal for local development.
  return `${globalThis.location.origin}${fallbackPath}`;
};


/**
 * Get Main Backend API Base URL
 * @returns {string} API base URL
 */
export const getApiBaseUrl = () => {
  return resolveUrl('VUE_APP_API_BASE_URL', '/apimain');
};

/**
 * Get WebSocket URL
 * @returns {string} WebSocket URL
 */
export const getWebSocketUrl = () => {
  const envUrl = process.env.VUE_APP_WEB_SOCKET;

  // Check if environment variable is a valid WebSocket URL
  if (envUrl && (envUrl.startsWith('ws://') || envUrl.startsWith('wss://'))) {
    // Ensure it's a complete URL
    try {
      new URL(envUrl);
      return envUrl;
    } catch (e) {
      console.warn(`VUE_APP_WEB_SOCKET="${envUrl}" is not a valid URL. Attempting to convert from HTTP/HTTPS.`);
    }
  }
  
  // If it's an HTTP/HTTPS URL, convert it to WS/WSS
  if (envUrl && (envUrl.startsWith('http://') || envUrl.startsWith('https://'))) {
    try {
      const url = new URL(envUrl);
      const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${url.host}${url.pathname}`;
      return wsUrl;
    } catch (e) {
      console.warn(`VUE_APP_WEB_SOCKET="${envUrl}" could not be converted to WebSocket URL. Falling back to derived URL.`);
    }
  }
  
  // Fallback: derive WebSocket URL from current page location
  if (typeof globalThis !== 'undefined' && globalThis.location) {
    const protocol = globalThis.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = globalThis.location.host;
    
    // Ensure we have a valid protocol and host
    if (protocol && host) {
      const wsUrl = `${protocol}//${host}`;
      // Only log warning if envUrl was provided but invalid
      if (envUrl) {
        console.warn(`VUE_APP_WEB_SOCKET="${envUrl}" is not a valid ws:// or wss:// URL. Falling back to ${wsUrl}`);
      }
      return wsUrl;
    }
  }
  
  // Last resort fallback
  console.error('Unable to determine WebSocket URL. Using default ws://localhost');
  return 'ws://localhost';
};

/**
 * Get Market API Base URL
 * @returns {string} Market API base URL
 */
export const getMarketApiBaseUrl = () => {
  return resolveUrl('VUE_APP_MARKET_API_BASE_URL', '/apimarket/api');
};

/**
 * Get Forums API URL
 * @returns {string} Forums API URL
 */
export const getForumsApiUrl = () => {
  return resolveUrl('VUE_APP_FORUMS_API', '/apiforums/v1');
};

/**
 * Get Events API URL
 * @returns {string} Events API URL
 */
export const getEventsApiUrl = () => {
  return resolveUrl('VUE_APP_EVENTS', '/apievents');
};

/**
 * Get Todo API URL
 * @returns {string} Todo API URL
 */
export const getTodoApiUrl = () => {
  return resolveUrl('VUE_APP_TODO_API_BASE_URL', '/apitodo');
};